import { usePathname, useRouter } from "expo-router"
import React from "react"
import { StyleSheet } from "react-native"
import { useStyle } from "react-native-style-utilities"

import { ENVIRONMENT, isTargetDatabase } from "@/base/environment"
import { FarmHqLogo } from "@/components/FarmHqLogo"
import {
  AppText,
  Box,
  COLORS,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Pressable,
  Row,
  SIZES,
  SPACING,
  useIsDarkMode,
  useScreenSizeBreakpoint,
  View,
  Z_INDEX,
} from "@/theme"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"

import type { AppbarPage } from "./dashboard-pages"
import { useTheme } from "@react-navigation/native"

const styles = StyleSheet.create({
  logo: {
    marginVertical: "auto",
    minHeight: SIZES.$16,
    width: SIZES.$28,
  },
  menuContent: {
    alignSelf: "flex-end",
    marginRight: SPACING.$4,
    minWidth: SIZES.$56,
    position: "absolute",
    right: 0,
    top: SIZES.$toolbar,
  },
  pageLink: {
    marginHorizontal: SPACING.$2,
  },
  root: {
    height: SIZES.$toolbar,
    paddingHorizontal: SPACING.$4,
    shadowColor: COLORS.$black,
    shadowOffset: { height: 2, width: 0 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    width: "100%",
    zIndex: Z_INDEX.appBar,
  },
})

/**
 * Hamburger menu for small/medium screens.
 * Shows only page links for navigation.
 */
function HamburgerMenu({ pages }: { pages: AppbarPage[] }): React.JSX.Element {
  const [isOpen, setIsOpen] = React.useState(false)
  const router = useRouter()
  const pathName = usePathname()
  const isDark = useIsDarkMode()

  const handleCloseMenu = () => setIsOpen(false)

  let linkColor = COLORS.$gray[500]
  let selectedColor = COLORS.$blue[600]
  // Use yellow for the hamburger icon in dark mode
  const menuIconColor = isDark ? COLORS.$primary[500] : undefined
  if (isDark) {
    linkColor = COLORS.$gray[200]
    selectedColor = COLORS.$primary[500]
  }

  return (
    <React.Fragment>
      <Box ml={"auto"}>
        <IconButton
          IconComponent={"Menu"}
          iconColor={menuIconColor}
          id={"navMenuToggleBtn"}
          size={"lg"}
          variant={"ghost"}
          onPress={() => setIsOpen(true)}
        />
      </Box>
      <Menu
        id={"navMenu"}
        isOpen={isOpen}
        style={styles.menuContent}
        onClose={handleCloseMenu}
      >
        {pages.map((link, index) => {
          const isMatch = link.getIsMatch(pathName)
          const color = isMatch ? selectedColor : linkColor
          return (
            <React.Fragment key={link.id}>
              <Pressable
                trackHover
                trackPress
                id={`${link.id}Link`}
                role={"link"}
                onPress={() => {
                  handleCloseMenu()
                  router.navigate(link.href)
                }}
              >
                <MenuItem
                  IconComponent={link.IconComponent}
                  color={color}
                  text={link.text}
                />
              </Pressable>
              {index < pages.length - 1 ? <Divider /> : null}
            </React.Fragment>
          )
        })}
      </Menu>
    </React.Fragment>
  )
}

export default function Appbar(props: {
  pages: AppbarPage[]
}) {
  const pages = props.pages
  const router = useRouter()
  const pathName = usePathname()
  const currentScreenSize = useScreenSizeBreakpoint()

  const isDark = useIsDarkMode()
  const navTheme = useTheme()

  let linkColor = COLORS.$gray[500]
  let selectedColor = COLORS.$blue[600]
  let backgroundColor = navTheme.colors.card
  if (isDark) {
    linkColor = COLORS.$gray[200]
    selectedColor = COLORS.$primary[600]
    backgroundColor = navTheme.colors.card
  }

  const isLargeScreen =
    currentScreenSize === "lg" || currentScreenSize === "xl"

  return (
    <View
      id={"appBar"}
      style={useStyle(
        () => [styles.root, { backgroundColor }],
        [backgroundColor],
      )}
    >
      <Row
        alignItems={"center"}
        flexWrap={"nowrap"}
        justifyContent={"space-between"}
        minH={"$toolbar"}
      >
        <Box
          h={"$full"}
          maxW={"$48"}
          overflow={"visible"}
          position={"relative"}
        >
          <Pressable role={"link"} onPress={() => router.navigate("/")}>
            <FarmHqLogo
              name={"farmhq-logo-inline.png"}
              resizeMode={"contain"}
              style={styles.logo}
            />
          </Pressable>
          <Box bottom={"$2"} position={"absolute"} right={"$0"}>
            <AppText colorScheme={"secondary"} fontSize={"$2xs"}>
              {ENVIRONMENT.appVersion}
            </AppText>
          </Box>
          <Box bottom={"$2"} left={"$7"} position={"absolute"}>
            {isTargetDatabase("PROD") ? null : (
              <AppText colorScheme={"secondary"} fontSize={"$2xs"}>
                {ENVIRONMENT.targetDatabaseName}
              </AppText>
            )}
          </Box>
        </Box>
        {isLargeScreen ? (
          <Row alignItems={"flex-start"} ml={"auto"}>
            {pages.map(({ getIsMatch, ...link }) => {
              const isMatch = getIsMatch(pathName)
              const color = isMatch ? selectedColor : linkColor
              return (
                <Pressable
                  key={link.id}
                  accessibilityLabel={`Link to ${link.id}`}
                  accessibilityRole={"link"}
                  id={link.id}
                  role={"link"}
                  style={styles.pageLink}
                  onPress={() => router.navigate(link.href)}
                >
                  <Row alignItems={"center"}>
                    <Box mr={"$1"}>
                      {renderIconFromIconProp(link.IconComponent, {
                        color,
                        size: 18,
                      })}
                    </Box>
                    <AppText
                      color={color}
                      fontSize={"$sm"}
                      selectable={false}
                      underline={isMatch}
                    >
                      {link.text}
                    </AppText>
                  </Row>
                </Pressable>
              )
            })}
          </Row>
        ) : (
          <HamburgerMenu pages={pages} />
        )}
      </Row>
    </View>
  )
}
